import { useState } from 'react';

import { generateUniqueId, INIT_ENCOUNTER_NAME, sortedMonsterList, getImageUrl } from '../../constants';
import EncounterImage from '../../pics/icons/importDndBEncounter.png'
import { ImportDndBeyondCharacters } from '../../api/ImportDndBeyondCharacters'
import { ImportDndBeyondEncounter } from '../../api/ImportDndBeyondEncounter'
import { ImportDndBeyondMonsters } from '../../api/ImportDndBeyondMonsters'
import { DndBMonsterToDmBMapper } from '../../mappers/DndBMonsterToDmBMapper'
import { useImportedPlayers } from '../../../../../providers/ImportedPlayersProvider';
import { useHomebrewProvider } from '../../../../../providers/HomebrewProvider';
import { useUser } from '../../../../../providers/UserProvider';
import { RotatingLines } from 'react-loader-spinner';

function InputEncounterId({setCurrentEncounter, encounterGuid, setError, socket, onClick=() => {}}) { 
    const [dndbEncounterId, setDndbEncounterId] = useState('');
    const [loading, setLoading] = useState(false);
    const eGuid = encounterGuid || generateUniqueId();
    const {addImportedPlayers} = useImportedPlayers();
    const {addToHomebrewList} = useHomebrewProvider();
    
    const {username} = useUser();

    const handleDndBEncounterId = (event) => {
        let input = event.target.value;
        // Remove all characters that are NOT numbers letters, hyphens or commas
        input = input.replace(/[^a-z0-9,-]/g, '');

        if(input !== event.target.value) {
            // Set error if input contains invalid characters
            setError(1)
        }

        setDndbEncounterId(input)
    };  

    const handleDndEncounterImport = async () => {
        try {
            setLoading(true)
            // Matches a GUID/UUID of the form: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
            const isGuidRegex = /^[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}$/;
            if(isGuidRegex.test(dndbEncounterId)) {
                const data = await ImportDndBeyondEncounter(dndbEncounterId);
                if(data) {
                    const {monsters, players} = data.data;
                    // Turn the players objects into an array of numbers to match user input
                    const playerIds = players.map(player => player.id);
                    const dmbPlayers = await ImportDndBeyondCharacters(playerIds, eGuid, players, username);
                    addImportedPlayers(dmbPlayers)

                    // Send the whole monsters object since it comes with hp data
                    const dmbMonsters = await ImportDndBeyondMonsters(monsters, eGuid);

                    // Build mappedMonsters array with proper async/await
                    let mappedMonsters = dmbMonsters
                    for (const dndbMonster of monsters) {
                        let foundMonsters = sortedMonsterList.filter(sortedMonster => sortedMonster.name.toLowerCase() === dndbMonster.name.toLowerCase())
                        let groupGuid = foundMonsters.length === 0 ? null : generateUniqueId() 
                        // Add all monsters that match, could be from several versions - if so add the source to the name (dm can delet the ones they dont want)
                        for (const foundMonster of foundMonsters) {
                            let newMonster = {...foundMonster}
                            let avatarUrl = await getImageUrl(newMonster.name, newMonster.creature_type)
                            newMonster.avatarUrl = avatarUrl
                            
                            // DndB is 0 when creature is from a purchased source so use found creature since it has hp already
                            if(dndbMonster.maximumHitPoints !== 0 && dndbMonster.currentHitPoints !== 0) {
                                newMonster.hit_points_current = dndbMonster.currentHitPoints
                                newMonster.hit_points = dndbMonster.maximumHitPoints
                            }
                            
                            // Get data from dndbeyond
                            newMonster.initiative = dndbMonster.initiative
                            newMonster.hit_points_temp = dndbMonster.temporaryHitPoints
                            newMonster.creatureGuid = generateUniqueId()
                            newMonster.encounterGuid = eGuid

                            // Specify difference if more then one match is found
                            if(foundMonsters.length > 1) {
                                newMonster.name = newMonster.name + " - " + newMonster.sourceShort
                                newMonster.groupGuid = groupGuid // Can be null
                            }

                            mappedMonsters.push(newMonster)
                        }
                    }

                    setCurrentEncounter(prev => {
                        let encounterName = prev.encounterName;
                        if(encounterName === INIT_ENCOUNTER_NAME) {
                            encounterName = data?.data?.campaign?.name || 'DndBeyond Encounter - Imported'
                            if(prev.creatures.length === 0) {
                                socket.emit("newEncounter", eGuid, encounterName, prev.folder_dir)
                            }
                        }

                        const validPlayers = dmbPlayers.filter(player => player.status === 200);
                        socket.emit("importedDndBCreatures", validPlayers, username);

                        mappedMonsters.forEach(c => {
                            socket.emit("addCreatureToEncounter", c)
                        });

                        return {
                            ...prev,
                            encounterGuid: eGuid,
                            encounterName: encounterName,
                            creatures: [...prev.creatures, ...validPlayers, ...mappedMonsters]
                        };
                    });
                    console.log("Creatures Imported!")
                }
            } else {
                // const isDndbHomebrewRegex = /^[0-9]{7}$/;
                const ids = dndbEncounterId.split(',').map(id => id.trim()).filter(Boolean);
                // 6 to 7 characters long
                const isValidDndbIds = ids.length > 0 && ids.every(id => /^[0-9]{6,7}$/.test(id));

                if (isValidDndbIds) {
                    const monsters = ids.map(id => ({ id }));
                    const mappedMonsters = await ImportDndBeyondMonsters(monsters, eGuid);

                    setCurrentEncounter(prev => {
                        let encounterName = prev.encounterName;
                        if(encounterName === INIT_ENCOUNTER_NAME) {
                            encounterName = 'DndBeyond Homebrew - Imported'
                            if(prev.creatures.length === 0) {
                                socket.emit("newEncounter", eGuid, encounterName, prev.folder_dir)
                            }
                        }

                        mappedMonsters.forEach(c => {
                            socket.emit("addCreatureToEncounter", c)
                            addToHomebrewList({...c})

                        });

                        return {
                            ...prev,
                            encounterGuid: eGuid,
                            encounterName: encounterName,
                            creatures: [...prev.creatures, ...mappedMonsters]
                        };
                    });

                    console.log("Homebrew Imported!", mappedMonsters)
                } else {
                    // If not guid or valid comma-separated ids set some error
                    setError(2)
                }
            }
        } catch (error) {
            console.warn(error)
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 500);
        }
    }

    return (
        <div className='dndBImportContainer'>
            <img src={EncounterImage} alt="Click to Upload" className="menuIconImports" onClick={onClick}/>
            <div className='dndBImportButtons'>
                <input
                    className='dndbInput'
                    type="text"
                    placeholder='Encounter/Homebrew ID'
                    value={dndbEncounterId}
                    onChange={handleDndBEncounterId}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            handleDndEncounterImport();
                        }
                    }}
                />
                <div>
                    {dndbEncounterId.length !== 0 && 
                        <button className='dndbInputButtonSearch' onClick={() => handleDndEncounterImport()}
                            style={loading ? { pointerEvents: 'none' } : {}}
                            disabled={loading}
                        > 
                            {loading ? (
                                <RotatingLines
                                    width="20"
                                    strokeWidth="3"
                                    strokeColor="#000000"
                                    ariaLabel="importLoading"
                                />
                            ) : (
                                <>🔍</> 
                            )} 
                        </button>
                    }
                </div>
            </div>
        </div>
    );
}

export default InputEncounterId;
